import type { PitchResult } from '../types';
import { getAudioFrame } from './analyzer';

let smoothedFreq = 0;
let smoothedClarity = 0;
const SMOOTHING = 0.85;
const MIN_AMPLITUDE = 0.02;
const CONFIDENCE_THRESHOLD = 0.9;
const MIN_FREQ = 80;
const MAX_FREQ = 1200;

export function detectPitch(): PitchResult | null {
  const frame = getAudioFrame();
  if (!frame) return null;

  const freq = autocorrelationPitch(frame.timeDomainData, frame.sampleRate);

  if (freq < MIN_FREQ || freq > MAX_FREQ) {
    smoothedFreq *= SMOOTHING;
    smoothedClarity *= SMOOTHING;
    return { frequency: 0, clarity: smoothedClarity, amplitude: frame.rmsAmplitude };
  }

  smoothedFreq = smoothedFreq * SMOOTHING + freq * (1 - SMOOTHING);
  smoothedClarity = smoothedClarity * SMOOTHING + 1.0 * (1 - SMOOTHING);

  const hasPitch = smoothedClarity > CONFIDENCE_THRESHOLD && frame.rmsAmplitude > MIN_AMPLITUDE;

  return {
    frequency: hasPitch ? smoothedFreq : 0,
    clarity: smoothedClarity,
    amplitude: frame.rmsAmplitude,
  };
}

function autocorrelationPitch(buf: Float32Array, sampleRate: number): number {
  const SIZE = buf.length;
  let rms = 0;
  for (let i = 0; i < SIZE; i++) {
    const val = buf[i];
    rms += val * val;
  }
  rms = Math.sqrt(rms / SIZE);
  if (rms < 0.01) return 0;

  let r1 = 0;
  let r2 = SIZE - 1;
  const threshold = 0.12;

  for (let i = 0; i < SIZE / 2; i++) {
    if (Math.abs(buf[i]) < threshold) { r1 = i; break; }
  }
  for (let i = 1; i < SIZE / 2; i++) {
    if (Math.abs(buf[SIZE - i]) < threshold) { r2 = SIZE - i; break; }
  }

  buf = buf.slice(r1, r2);
  const newSize = buf.length;

  const c = new Float32Array(newSize);
  for (let i = 0; i < newSize; i++) {
    for (let j = 0; j < newSize - i; j++) {
      c[i] += buf[j] * buf[j + i];
    }
  }

  let d = 0;
  while (c[d] > c[d + 1]) d++;
  let maxval = -1;
  let maxpos = -1;
  for (let i = d; i < newSize; i++) {
    if (c[i] > maxval) {
      maxval = c[i];
      maxpos = i;
    }
  }
  let T0 = maxpos;

  // Parabolic interpolation
  const x1 = c[T0 - 1] || 0;
  const x2 = c[T0];
  const x3 = c[T0 + 1] || 0;
  const a = (x1 + x3 - 2 * x2) / 2;
  const b = (x3 - x1) / 2;
  if (a) T0 = T0 - b / (2 * a);

  return sampleRate / T0;
}
